import { Component, Event, EventEmitter, JSX, Prop, State, Watch, h } from '@stencil/core'
import FlexSearch from 'flexsearch'

import { BusinessClass, SearchableBusinessClass } from './air-business-class.types'
import { Validation } from '../../types/status.types'

@Component({
  tag: 'air-business-class',
  styleUrl: 'air-business-class.scss',
  shadow: true,
})
export class AirBusinessClass {
  /** Value of business class */
  @Prop({ mutable: true }) value?: string

  /** Specifies validation of business class */
  @Prop() validation?: Validation

  /** Specifies if business class is required */
  @Prop() required = false

  /** Specifies label */
  @Prop() label?: string

  /** Specifies placeholder value */
  @Prop() placeholder?: string

  @State() searchInputValue = ''
  @State() visibleResults = 5
  @State() searchableBusinessClasses: BusinessClass[] = []

  @State() majorOptions: BusinessClass[] = []
  @Watch('majorOptions')
  onMajorOptionsChange(): void {
    const selectedCobHasMinorOptions: boolean = !!this.majorSelected && this.minorOptions.length > 0
    this.showCannotFindMyBusiness =
      this.majorOptionsOpen && (!this.majorSelected || selectedCobHasMinorOptions)
    this.visibleResults = this.defaultResultsQuantity
  }

  /** Business Class list */
  @Prop({ mutable: true }) businessClasses?: BusinessClass[]
  @Watch('businessClasses')
  watchBusinessClassesChange(businessClasses: BusinessClass[]): void {
    this.initSearchableBusinessClasses(businessClasses)
    this.initOptions()
    if (businessClasses.length > 0 && this.value !== undefined) this.selectCobById(this.value)
    if (!businessClasses.find((businessClass) => businessClass.code === this.value)) {
      this.value = undefined
    }
  }

  @State() majorOptionsOpen = false
  @State() majorSelected: BusinessClass | null = null
  @State() showCannotFindMyBusiness = true

  /** Emits when business class value was changed */
  @Event({ eventName: 'air-business-class-change' }) change!: EventEmitter<string | null>

  @State() minorOptions: BusinessClass[] = []
  @Watch('minorOptions')
  onMinorOptionsChange(newValue: BusinessClass[]): void {
    if (newValue.length === 1) {
      const firstMinorOption = this.minorOptions[0].ancestorTitles
        ? this.minorOptions[0].ancestorTitles[0]
        : ''
      this.minorOptions = [
        ...newValue,
        {
          code:
            this.businessClasses?.find((option) => option.title === firstMinorOption)?.code ||
            this.notClassifiedCode,
          title: 'Other',
        },
      ]
    }
  }

  private defaultResultsQuantity = 5
  private notClassifiedCode = '000000'
  private searchIndex?: FlexSearch.Document<BusinessClass>

  private getMajorCode = (minorCode: string): string => minorCode.split('-').slice(0, 2).join('-')

  private isMinorClassWithoutAncestor =
    (businessClasses: BusinessClass[]) =>
    (cob: BusinessClass): boolean => {
      const { code } = cob
      if (code.split('-').length !== 3) {
        return false
      }
      return businessClasses.findIndex((cob) => cob.code === this.getMajorCode(code)) === -1
    }

  private createMajorAncestorClass = (businessClasses: BusinessClass): BusinessClass => ({
    code: this.getMajorCode(businessClasses.code),
    title: businessClasses.ancestorTitles?.[1] || '',
    ancestorTitles: [businessClasses.ancestorTitles?.[0] || ''],
  })

  private fillMissingMajorClasses(businessClasses: BusinessClass[]): BusinessClass[] {
    const minorClassesByMajorClassCode = new Map<string, BusinessClass>()
    businessClasses
      .filter(this.isMinorClassWithoutAncestor(businessClasses))
      .forEach((cob) => minorClassesByMajorClassCode.set(this.getMajorCode(cob.code), cob))
    minorClassesByMajorClassCode.forEach((cob) => {
      const majorClass = this.createMajorAncestorClass(cob)
      businessClasses.push(majorClass)
    })
    return businessClasses
  }

  private initSearchableBusinessClasses(businessClasses: BusinessClass[] | undefined): void {
    if (businessClasses && businessClasses.length > 0) {
      this.businessClasses = this.fillMissingMajorClasses(businessClasses)
      // for each minor cob that have appetite, create a major cob to be searchable
      const searchableBusinessClasses: BusinessClass[] = this.businessClasses.map(
        (item: BusinessClass) => {
          const naicsCode = item.code.split('-')[0]
          const majorCode =
            item.code.split('-')[1] !== undefined ? `${item.code.split('-')[1]}` : ''
          const parentMajorTitle =
            item.ancestorTitles && typeof item.ancestorTitles[1] === 'string'
              ? item.ancestorTitles[1]
              : ''
          const isMinor = item.code.split('-').length === 3
          const title = (isMinor ? parentMajorTitle : item.title) || ''
          const childrenTitles =
            this.businessClasses
              ?.map((businessClass) =>
                businessClass?.ancestorTitles?.includes(title) &&
                businessClass.code.split('-')[0] === item.code.split('-')[0]
                  ? businessClass.title
                  : '',
              )
              .filter((title) => title !== '') || []

          return {
            code: `${naicsCode}${majorCode.length > 0 ? '-' : ''}${majorCode}`,
            title: title,
            ancestorTitles: [
              item.ancestorTitles && typeof item.ancestorTitles[0] === 'string'
                ? item.ancestorTitles[0]
                : '',
            ],
            childrenTitles: childrenTitles,
          }
        },
      )
      // create a list of unique business classes codes which can appear on the result list (no minor business classes)
      // the minor business class should only be available under other parent previously selected
      const uniqueSearchableCodes = [
        ...new Set(
          this.businessClasses.map((item) =>
            item.code.split('-').length === 3
              ? `${item.code.split('-')[0]}-${item.code.split('-')[1]}`
              : item.code,
          ),
        ),
      ]

      const isNAICSWithChildren = (cob: SearchableBusinessClass): boolean =>
        Array.isArray(cob.childrenTitles) &&
        cob.childrenTitles.length > 0 &&
        cob.code.split('-').length === 1

      // filter out duplicates and NAICS containing children because the children are searchable
      this.searchableBusinessClasses = uniqueSearchableCodes
        .map((cobCode) =>
          searchableBusinessClasses.find(
            (cob) => cob.code === cobCode && !isNAICSWithChildren(cob),
          ),
        )
        .filter((item): item is BusinessClass => !!item)
    }
  }

  componentWillLoad(): void {
    const isDeprecatedDataStructure = this.businessClasses?.some((item) => 'children' in item)

    if (isDeprecatedDataStructure) {
      throw new Error('Received deprecated business classes data!')
    }
    this.initSearchableBusinessClasses(this.businessClasses)

    this.initOptions()
  }

  private selectCobById(id: string): void {
    const businessClass = this.businessClasses?.find((option) => option.code === id)
    if (businessClass) {
      this.majorSelected = businessClass
      this.minorOptions = []
    } else if (/-\d{1,2}$/.test(id)) {
      this.minorOptions = []
    }
  }

  private createSearchIndex(): FlexSearch.Document<BusinessClass> {
    const index = new FlexSearch.Document<BusinessClass>({
      document: {
        id: 'code',
        index: [
          {
            field: 'title',
            resolution: 9,
            preset: 'match',
            tokenize: 'forward',
          },
          {
            field: 'code',
            resolution: 8,
            preset: 'score',
            tokenize: 'forward',
          },
          {
            field: 'childrenTitles',
            resolution: 5,
            preset: 'match',
            tokenize: 'forward',
          },
          {
            field: 'ancestorTitles',
            resolution: 4,
            preset: 'match',
            tokenize: 'forward',
          },
        ],
      },
    })
    for (const node of this.searchableBusinessClasses) {
      index.add(node)
    }
    return index
  }

  private initOptions = (): void => {
    this.searchIndex = this.createSearchIndex()
    this.dismissResults()
    this.selectCobById(this.value || '')
  }

  private handleChange = async (event: CustomEvent<string>): Promise<void> => {
    event.stopPropagation()
    this.searchInputValue = event.detail as string
    this.majorOptions = await this.searchHandler(this.searchInputValue)
    this.majorOptionsOpen = true
  }

  private handleMinorSelect = (event: UIEvent, businessClass: BusinessClass): void => {
    event.stopPropagation()
    this.change?.emit(businessClass.code || '')
  }

  private handleCannotFindMyBusiness = (event: MouseEvent): void => {
    event.stopPropagation()
    const cannotFindMyBusinessOption = { title: 'Not otherwise classified', code: '000000' }
    this.handleMajorSelect(cannotFindMyBusinessOption)
    this.showCannotFindMyBusiness = false
  }

  private async searchHandler(searchInputValue: string): Promise<BusinessClass[]> {
    // get search results as ids of business classes for each field searched
    const searchedIndex = await this.searchIndex?.search(searchInputValue, {
      enrich: true,
      suggest: true,
    })
    // remove duplicates from results
    const uniqueIdsFound = [
      ...new Set(
        searchedIndex
          ?.map((searchIndexFieldResult) => searchIndexFieldResult.result)
          .flatMap((cobId) => cobId),
      ),
    ]

    // return business classes by ids
    return uniqueIdsFound
      .map((id) =>
        this.businessClasses?.find(
          (businessClass) => businessClass && businessClass.code === id.toString(),
        ),
      )
      .filter((item): item is BusinessClass => !!item)
  }

  private handleSelect = (event: CustomEvent<BusinessClass>): void => {
    const businessClass = event.detail as BusinessClass
    this.handleMajorSelect(businessClass)
  }

  private handleMajorSelect = (businessClass: BusinessClass): void => {
    this.majorSelected = businessClass
    this.majorOptionsOpen = false

    this.minorOptions =
      this.businessClasses?.filter((option) =>
        option.ancestorTitles?.includes(businessClass.title),
      ) || []

    if (this.minorOptions.length === 0) {
      this.change?.emit(businessClass.code)
    }
  }

  private resetDefaultState = (): void => {
    if (this.searchInputValue.length > 0) this.majorOptionsOpen = true
    this.minorOptions = []
    this.visibleResults = this.defaultResultsQuantity
    this.majorSelected = null
  }

  private dismissResults = (): void => {
    this.searchInputValue = ''
    this.majorOptions = []
    this.majorOptionsOpen = false
    this.resetDefaultState()
  }

  private handleShowMoreOrLess = (): void => {
    if (
      this.visibleResults > this.majorOptions.length ||
      this.visibleResults === this.majorOptions.length
    ) {
      this.visibleResults = this.defaultResultsQuantity
    } else {
      this.visibleResults = this.visibleResults + 5
    }
  }

  private renderNoResults = (): JSX.Element => {
    return <p class="no-results">No results, please try different keyword</p>
  }

  private renderDismissResults = (): JSX.Element => {
    return (
      <span
        onClick={this.dismissResults}
        class="air-link air-link-navigation results-dismiss-link"
        tabindex="0"
      >
        <air-icon name="x"></air-icon>
        Dismiss results
      </span>
    )
  }

  private renderShowMoreOrLess = (): JSX.Element => {
    return (
      <air-button onClick={this.handleShowMoreOrLess} variant="secondary">
        {this.visibleResults > this.majorOptions.length ||
        this.visibleResults === this.majorOptions.length
          ? 'Show less'
          : 'Show more'}
      </air-button>
    )
  }

  private renderBusinessClasses = (options: BusinessClass[]): JSX.Element[] => {
    return options
      .map((businessClass) => {
        return (
          <air-business-class-card
            onAir-select={this.handleSelect}
            businessClass={businessClass}
          ></air-business-class-card>
        )
      })
      .slice(0, this.visibleResults)
  }

  private renderNAICSHelpTextPlaceholder = (): JSX.Element => {
    return (
      <span class="input-hint">{/^-?\d+$/.test(this.searchInputValue || '') && 'NAICS code'}</span>
    )
  }

  render(): JSX.Element {
    if (!this.businessClasses || this.businessClasses.length === 0) {
      throw new Error('Business classes are not defined!')
    }
    const classes = {
      'business-class': true,
      [`${this.validation?.status}`]: this.validation?.status !== undefined,
    }

    const attributes = {
      class: 'cob-input-search',
      label: this.label,
      icon: 'search',
      placeholder: this.placeholder,
      'onAir-change': this.handleChange,
      'onAir-input': this.handleChange,
      value: this.searchInputValue,
      validation: this.validation,
    }

    return (
      <div class={classes}>
        <air-input {...attributes}></air-input>
        {this.renderNAICSHelpTextPlaceholder()}
        {this.majorOptionsOpen && (
          <div>
            {this.majorOptions.length > 0 && (
              <div class="results-header-wrapper">
                <span class="results-header-title"> Select your business type </span>
                <span class="results-header-counter">{this.majorOptions.length} results found</span>
              </div>
            )}
            {this.majorOptions.length > 0
              ? this.renderBusinessClasses(this.majorOptions)
              : this.renderNoResults()}
            {this.majorOptions.length > 5 && this.renderShowMoreOrLess()}
            {this.majorOptions.length > 0 && this.renderDismissResults()}
          </div>
        )}
        {this.majorSelected && !this.majorOptionsOpen && (
          <div class="result-selected">
            <div class="results-header-wrapper back-to-results" onClick={this.resetDefaultState}>
              <span tabindex="0" class="air-link air-link-navigation">
                <air-icon name="chevron-left"></air-icon>
                Back to results
              </span>
            </div>
            <air-business-class-card businessClass={this.majorSelected}>
              {this.minorOptions.length > 0 && (
                <div>
                  <p class="business-class-card-caption">Please specify</p>
                  {this.minorOptions.map((businessClass) => {
                    return (
                      <air-radio-button
                        // eslint-disable-next-line react/jsx-no-bind
                        onClick={(event: UIEvent): void =>
                          this.handleMinorSelect(event, businessClass)
                        }
                        label={businessClass.title}
                        name={`business-class-${this.majorSelected?.code.toString()}`}
                        value={businessClass.code}
                      ></air-radio-button>
                    )
                  })}
                </div>
              )}
            </air-business-class-card>
          </div>
        )}
        {this.showCannotFindMyBusiness && (
          <div
            class="results-footer cannot-find-my-business"
            onClick={this.handleCannotFindMyBusiness}
          >
            <air-divider></air-divider>
            <span class="air-link air-link-navigation" tabindex="0">
              If you cannot find your business, tell us more here.
            </span>
          </div>
        )}
      </div>
    )
  }
}
